/**
 * A pressable whose visible surface neither the host app's CSS nor React Native's layout engine
 * can take away.
 *
 * ```tsx
 * <PressableSurface onPress={onClose} accessibilityRole="button" surface={styles.closeButton}>
 *   <Text style={styles.glyph}>{"✕"}</Text>
 * </PressableSurface>
 * ```
 *
 * 🔴 **The two platforms need OPPOSITE structures**, which is why this is a folder:
 *
 * | | the chrome goes on | because |
 * |---|---|---|
 * | web (`index.desktop.tsx`) | an inner `<View>` | the `<button>` is the element the host's CSS names |
 * | native (`index.native.tsx`) | the `Pressable` itself | an inner growing `<View>` stretches the button |
 *
 * ## Why web needs the inner View
 *
 * 🔴 **A `Pressable` with `accessibilityRole="button"` renders under react-native-web as a real
 * `<button type="button">`, and `apps/web`'s global CSS names that element BY TAG.** Tailwind's
 * preflight ships:
 *
 * ```css
 * [type=button],[type=reset],[type=submit],button{background-color:transparent;background-image:none}
 * ```
 *
 * `[type=button]` is an ATTRIBUTE selector, so its specificity is (0,1,0) — exactly that of
 * react-native-web's atomic `.r-backgroundColor-…` class. A tie is broken by DOCUMENT ORDER, and
 * **RNW deliberately inserts its stylesheet as the FIRST child of `<head>`** so that host CSS can
 * override it — `head.insertBefore(element, head.firstChild)` in
 * `react-native-web/dist/exports/StyleSheet/dom/createCSSStyleSheet.js`. Every app stylesheet is
 * therefore later, wins the tie, and **the background is erased.** A filled control keeps its
 * label colour and renders as blank space: that is how the loyalty panel shipped with an
 * invisible "Apply Points" and an invisible "Verify".
 *
 * ⚠️ This used to say RNW "injects its sheet at runtime while the app's stylesheet is already in
 * the document" — i.e. that RNW lost by arriving later. It is the other way round, and the
 * difference is not pedantry: RNW puts itself first ON PURPOSE, so "load the sheets in the other
 * order" is not a fix and never was. Moving the paint off the tag is the only answer.
 *
 * ⚠️ The same applies to a `TextInput`, which RNW renders as a real `<input>`: put a field's
 * border and fill on a wrapper `<View>` and leave size, padding and typography on the field.
 *
 * ## Why native must NOT have it
 *
 * 🔴 **React Native lays out with Yoga under `YGErrataAll`** — `YogaLayoutableShadowNode.cpp`
 * passes it as the default errata — and that includes `StretchFlexBasis`: a container which is
 * not sized EXACTLY keeps the whole of its at-most size instead of shrinking to its content, and
 * any `flexGrow` child then grows into all of it (`CalculateLayout.cpp`,
 * `useLegacyStretchBehaviour`). The web variant's inner View must grow to fill a button the
 * caller sized — so on native every content-sized button inflated to the tallest height its
 * parent would allow. The loyalty panel's CTAs came out 110dp tall instead of 36, hung out of
 * the card, were clipped by the host's `overflow: "hidden"`, and centred their labels far below
 * the text they belong to.
 *
 * ⚠️ **Web is unaffected** — a browser sizes an auto-height flex container to its content
 * whatever its children's `flex-grow` — so, exactly like the host-CSS defect above, this shipped
 * looking right on one platform. Neither jest nor react-native-web lays out with Yoga, so no test
 * here can catch it; `docs/LOYALTY_PANEL_HANDOFF.md` (ninth session) records how it was
 * reproduced against RN's own engine.
 *
 * There is no cascade on native, so there is nothing for an inner View to protect: the chrome
 * goes straight onto the `Pressable`, which is exactly the box the caller laid out — no taller.
 *
 * ## Using it
 *
 * - `surface` — everything visible: background, border, radius, padding, and the inner alignment
 *   of the label. This is where a style that used to sit in `style` belongs.
 * - `style` — only what positions the control among its SIBLINGS: `margin*`, `flex*`,
 *   `alignSelf`, `position`. Leaving those on the button keeps the surrounding layout identical.
 *
 * The split is explicit rather than inferred: a heuristic that guessed which half of a style
 * object was "chrome" would silently move a margin inside the button and shift every layout that
 * adopted it.
 *
 * 🔴 **Keep `style` to sibling layout on BOTH platforms.** Native has one element, so it applies
 * `[style, surface]` and the surface wins a clash — which is what web does in effect, where a
 * `justifyContent` on the `<button>` has nothing to move but the surface that fills it. But
 * anything in `style` the surface does not override — `padding`, `alignItems`, a
 * `backgroundColor` — lays out or paints the CHILDREN on native while doing nothing (or being
 * erased) on web. That is a per-platform difference, and the contract above is what avoids it.
 *
 * A control with no background and no border does not need this — a bare `Pressable` is fine, and
 * most of them are. Reach for it when the control is PAINTED.
 */
import type { ComponentPropsWithRef, ReactNode } from "react";
import type { Pressable, PressableProps, StyleProp, ViewStyle } from "react-native";

/** What `Pressable` hands its `style` callback, and all either variant reads from it. */
export interface PressState {
  pressed: boolean;
  /** web only: react-native-web's `Pressable` reports them, React Native's does not (DESIGN_SYSTEM.md §4.3) */
  hovered?: boolean;
  focused?: boolean;
}

export type PressableSurfaceStyle =
  StyleProp<ViewStyle> | ((state: PressState) => StyleProp<ViewStyle>);

export interface PressableSurfaceProps extends Omit<PressableProps, "style" | "children"> {
  /**
   * The control's visible chrome.
   *
   * Takes the `pressed` flag, like `Pressable`'s own `style`, so a pressed state can repaint the
   * surface rather than dimming the whole control.
   */
  surface?: PressableSurfaceStyle;
  /** Layout only — how this control sits among its siblings. See the header. */
  style?: PressableSurfaceStyle;
  /** the control itself, for a caller that moves focus to it (React 19 passes a ref as a prop) */
  ref?: ComponentPropsWithRef<typeof Pressable>["ref"];
  /** a node, or (as `Pressable` takes it) a function of the press state; both bodies pass the state */
  children?: ReactNode | ((state: PressState) => ReactNode);
}

/** A `style`/`surface` prop resolved against the current press state. */
export const resolvePressStyle = (
  style: PressableSurfaceStyle | undefined,
  state: PressState,
): StyleProp<ViewStyle> => (typeof style === "function" ? style(state) : style);
